import {
  buildCandidateWriteFailureMailto,
  executeCandidateWriteWithRetry,
  normalizeCandidateWriteError,
  reportCandidateWriteFailure,
} from "@/lib/candidateWriteReliability";

export interface CandidateWriteContext {
  operation: "assessment-answer" | "user-state";
  targetPath: string;
  blockId?: string;
}

interface CandidateWriteFailureTelemetry {
  correlationId: string;
  occurredAt: string;
  operation: CandidateWriteContext["operation"];
  targetPath: string;
  blockId?: string;
  siteId: string;
  profile: {
    id: string;
    name: string;
    emailAddress: string;
  };
  route: string;
  initialError: ReturnType<typeof normalizeCandidateWriteError>;
  retryError: ReturnType<typeof normalizeCandidateWriteError>;
}

const TELEMETRY_CONTENT_TYPE =
  "application/vnd.pivotalgroup.data.telemetry.client-write-failure+json";

function createCorrelationId(): string {
  return globalThis.crypto?.randomUUID?.()
    ?? `candidate-write-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function useCandidateWriteReliability() {
  const { site } = useSite();
  const { profile, getProfile } = useProfile();
  const route = useRoute();
  const { configuration } = useCandidateWriteFailureSettings();
  const { publish } = useCandidateWriteFailureNotification();

  async function resolveProfile() {
    return profile.value ?? await getProfile();
  }

  async function writeTelemetry(
    telemetry: CandidateWriteFailureTelemetry,
  ): Promise<void> {
    const fileName = `${telemetry.occurredAt.replace(/[:.]/g, "-")}-${telemetry.correlationId}`;
    await useApiFetch(`/v1/file/data/${telemetry.profile.id}/telemetry`, {
      method: "POST",
      headers: {
        "x-site-id": telemetry.siteId,
        "Content-Disposition":
          `form-data; name="telemetry"; filename="${fileName}"`,
        "Content-Type": TELEMETRY_CONTENT_TYPE,
        "x-mkdir": true,
      },
      body: JSON.stringify(telemetry),
    });
  }

  async function execute<T>(
    context: CandidateWriteContext,
    write: () => Promise<T>,
  ): Promise<T> {
    return executeCandidateWriteWithRetry({
      write,
      retryDelayMs: configuration.value.retryDelayMs,
      onExhausted: async (initialError, retryError) => {
        const correlationId = createCorrelationId();
        const occurredAt = new Date().toISOString();
        const currentIdentity = profile.value;
        const identity = {
          name: String(currentIdentity?.name ?? ""),
          emailAddress: String(currentIdentity?.emailAddress ?? ""),
          profileId: String(currentIdentity?.id ?? ""),
        };
        const normalizedRetryError = normalizeCandidateWriteError(retryError);

        await reportCandidateWriteFailure({
          notificationEnabled: configuration.value.notificationEnabled,
          writeTelemetry: async () => {
            const currentProfile = await resolveProfile();
            const siteId = String(site.value?.id ?? "").trim();
            if (!currentProfile?.id) {
              throw new Error(
                "A candidate profile is required to write failure telemetry.",
              );
            }
            if (!siteId) {
              throw new Error(
                "A site ID is required to write failure telemetry.",
              );
            }
            identity.name = String(currentProfile.name ?? "");
            identity.emailAddress = String(
              currentProfile.emailAddress ?? "",
            );
            identity.profileId = String(currentProfile.id);

            await writeTelemetry({
              correlationId,
              occurredAt,
              operation: context.operation,
              targetPath: context.targetPath,
              blockId: context.blockId,
              siteId,
              profile: {
                id: identity.profileId,
                name: identity.name,
                emailAddress: identity.emailAddress,
              },
              route: route.fullPath,
              initialError: normalizeCandidateWriteError(initialError),
              retryError: normalizedRetryError,
            });
          },
          publishNotification: (telemetryError) => {
            publish({
              id: correlationId,
              message: configuration.value.notificationMessage,
              contactEmail: configuration.value.contactEmail,
              mailtoHref: buildCandidateWriteFailureMailto(
                configuration.value.contactEmail,
                {
                  identity,
                  operation: context.operation,
                  targetPath: context.targetPath,
                  initialError: normalizeCandidateWriteError(initialError),
                  retryError: normalizedRetryError,
                  telemetryError:
                    normalizeCandidateWriteError(telemetryError),
                  networkOnline: globalThis.navigator?.onLine,
                  correlationId,
                  occurredAt,
                },
              ),
            });
          },
        });
      },
    });
  }

  return { execute };
}
